import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import { BROKER_CODES, BROKER_NAME_MAP, BrokerCode } from '../../../types/brokerTypes';
import { useMobxInstance } from '../../utils/mobxUtil';
import { useEffect } from 'react';

class ViewStore {
    selectedBrokers: Set<BrokerCode> = new Set();

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * 증권사 선택/해제 토글
     */
    toggleBroker = (brokerCode: BrokerCode) => {
        if (this.selectedBrokers.has(brokerCode)) {
            this.selectedBrokers.delete(brokerCode);
        } else {
            this.selectedBrokers.add(brokerCode);
        }
    };

    /**
     * 증권사가 선택되었는지 확인
     */
    isBrokerSelected = (brokerCode: BrokerCode): boolean => {
        return this.selectedBrokers.has(brokerCode);
    };

    /**
     * 초기값으로 특정 증권사 목록 설정
     */
    setSelectedBrokers = (brokers: BrokerCode[]) => {
        this.selectedBrokers = new Set(brokers);
    };

    /**
     * 선택된 증권사 배열로 반환
     */
    get selectedBrokersArray(): BrokerCode[] {
        return Array.from(this.selectedBrokers);
    }
}

const BrokerSelection = observer(
    ({
        selectedCodes = [],
        onSelectionChange,
        className,
    }: {
        selectedCodes?: BrokerCode[];
        onSelectionChange?: (brokers: BrokerCode[]) => void;
        className?: string;
    }) => {
        const viewStore = useMobxInstance(ViewStore);

        useEffect(() => {
            viewStore.setSelectedBrokers(selectedCodes);
        }, [selectedCodes]);

        const handleToggle = (brokerCode: BrokerCode) => {
            viewStore.toggleBroker(brokerCode);
            onSelectionChange?.(viewStore.selectedBrokersArray);
        };

        return (
            <div className={twMerge('grid grid-cols-2 gap-2', className)}>
                {BROKER_CODES.map((brokerCode) => {
                    const isSelected = viewStore.isBrokerSelected(brokerCode);

                    return (
                        <button
                            key={brokerCode}
                            type="button"
                            className={twMerge(
                                'btn btn-soft btn-block',
                                isSelected && 'btn-primary border-primary',
                            )}
                            onClick={() => handleToggle(brokerCode)}
                        >
                            {BROKER_NAME_MAP[brokerCode]}
                        </button>
                    );
                })}
            </div>
        );
    },
);

export default BrokerSelection;
